/**
 * Lightweight structured logger for the consultant portal frontend.
 *
 * Goals (see issue #547 — logging hygiene + Grafana Cloud readiness):
 * - Dev: pretty, colored `console.*` output so engineers still get the
 *   developer experience they expect.
 * - Prod: emit a single-line JSON object per call (`{ts, level, msg, ctx,
 *   release}`) so the nginx-tailing log shipper (Promtail → Loki →
 *   Grafana Cloud) can parse it without regex.
 * - Configurable level via `VITE_BONDIT_CONSULT_POR_LOG_LEVEL` (build-time)
 *   OR `window.RUNTIME_CONFIG.LOG_LEVEL` (runtime, injected by
 *   `docker-entrypoint.sh` into `config.js`). Runtime wins so we don't
 *   need to rebuild the image to flip log level — the SPA picks it up on
 *   the next page load.
 *
 * Levels: DEBUG < INFO < WARN < ERROR. Default = INFO.
 */

type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

const LEVEL_ORDER: Record<LogLevel, number> = {
  DEBUG: 10,
  INFO: 20,
  WARN: 30,
  ERROR: 40,
};

const isDev = import.meta.env.DEV;

const normalizeLevel = (value: unknown): LogLevel | null => {
  if (typeof value !== "string") {
    return null;
  }
  const upper = value.toUpperCase();
  if (upper in LEVEL_ORDER) {
    return upper as LogLevel;
  }
  return null;
};

const resolveLevel = (): LogLevel => {
  // Runtime-injected config wins (set by docker-entrypoint.sh from the
  // pod env without rebuilding the image).
  if (typeof window !== "undefined") {
    const runtime = normalizeLevel(window.APP_CONFIG?.LOG_LEVEL);
    if (runtime) {
      return runtime;
    }
  }
  const buildTime = normalizeLevel(import.meta.env.VITE_BONDIT_CONSULT_POR_LOG_LEVEL);
  if (buildTime) {
    return buildTime;
  }
  return "INFO";
};

const activeLevel: LogLevel = resolveLevel();
const release: string = import.meta.env.VITE_APP_VERSION || "dev";

const shouldLog = (level: LogLevel): boolean => LEVEL_ORDER[level] >= LEVEL_ORDER[activeLevel];

type LogContext = Record<string, unknown>;

// Pretty-print colors for dev (browser console understands %c).
const DEV_STYLES: Record<LogLevel, string> = {
  DEBUG: "color:#888;font-weight:bold",
  INFO: "color:#2563eb;font-weight:bold",
  WARN: "color:#d97706;font-weight:bold",
  ERROR: "color:#dc2626;font-weight:bold",
};

const emit = (level: LogLevel, msg: string, ctx?: LogContext): void => {
  if (!shouldLog(level)) {
    return;
  }
  if (isDev) {
    // ESLint's no-console rule only allows warn/error. For dev logging we
    // map debug/info onto warn (still flagged in dev tools by level prefix
    // in the message style) so we don't need to disable the lint rule.
    const fn = level === "ERROR" ? console.error : console.warn;
    if (ctx === undefined) {
      fn(`%c[${level}]%c ${msg}`, DEV_STYLES[level], "");
    } else {
      fn(`%c[${level}]%c ${msg}`, DEV_STYLES[level], "", ctx);
    }
    return;
  }

  // Prod: single-line JSON for the log shipper. We deliberately use
  // `console.log` so all levels land on stdout — nginx already separates
  // its access/error streams; the SPA's logs go through the browser
  // console which the user-side tooling collects only when explicitly
  // wired up. The JSON shape is what matters for Grafana parsing.
  const payload = {
    ts: new Date().toISOString(),
    level,
    msg,
    ctx: ctx ?? undefined,
    release,
  };
  // eslint-disable-next-line no-console
  console.log(JSON.stringify(payload));
};

export const logger = {
  debug: (msg: string, ctx?: LogContext): void => emit("DEBUG", msg, ctx),
  info: (msg: string, ctx?: LogContext): void => emit("INFO", msg, ctx),
  warn: (msg: string, ctx?: LogContext): void => emit("WARN", msg, ctx),
  error: (msg: string, ctx?: LogContext): void => emit("ERROR", msg, ctx),
  /** Exposed for tests / diagnostics only. */
  _level: activeLevel,
};

export type { LogLevel };
